import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'components/ui/form';
import { Label } from 'components/ui/label';
import { useToast } from 'hooks/use-toast';
import { useConnectTool, useStartToolOidc } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import { useMcpServiceChatStore } from 'state/store/chat/use-mcp-service-chat-store';
import { useChatStore } from 'state/store/chat/use-chat-store';

interface McpServiceAuthFormData {
  username: string;
  password: string;
}

/**
 * Opens when the chat stream emits `auth_required` for a service-tool.
 * `authMethod === "oidc"` opens the IdP authorization URL in a popup and waits for the
 * callback `postMessage`; otherwise it collects per-user credentials and POSTs them.
 * On success the pending chat turn is auto-resumed.
 */
export function McpServiceAuthModal() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const pendingAuth = useMcpServiceChatStore((state) => state.pendingAuth);
  const clearPendingAuth = useMcpServiceChatStore((state) => state.clearPendingAuth);
  const { mutateAsync: connectTool, isPending } = useConnectTool();
  const { mutateAsync: startToolOidc, isPending: isStartingOidc } = useStartToolOidc();
  const activeConversationId = useChatStore((state) => state.activeConversationId);

  const isOidc = pendingAuth?.authMethod === 'oidc';

  const form = useForm<McpServiceAuthFormData>({
    mode: 'onChange',
    defaultValues: { username: '', password: '' },
  });
  const { control, handleSubmit, reset } = form;

  useEffect(() => {
    if (pendingAuth) {
      reset({ username: '', password: '' });
    }
  }, [pendingAuth, reset]);

  // OIDC: listen for the callback page postMessage, then auto-resume the pending turn.
  useEffect(() => {
    if (!pendingAuth || !isOidc) return;

    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; ok?: boolean; toolId?: string } | undefined;
      if (data?.type !== 'mcp-oauth' || !data.ok) return;
      if (data.toolId && data.toolId !== pendingAuth.mcpServiceId) return;
      void afterConnected();
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAuth, isOidc]);

  if (!pendingAuth) {
    return null;
  }

  const afterConnected = async () => {
    await queryClient.invalidateQueries({ queryKey: ['tools', 'connection', pendingAuth.mcpServiceId] });
    toast({
      variant: 'success',
      title: t('CONNECTED') || 'Connected',
      description:
        t('MCP_SERVICE_CONNECTED_SUCCESS', {
          name: pendingAuth.mcpServiceName,
          defaultValue: 'Connected to {{name}}.',
        }) || 'Connected.',
    });
    const { resume } = useMcpServiceChatStore.getState();
    console.log('[MCP_RESUME_DEBUG] modal afterConnected — resume defined?', typeof resume === 'function');
    clearPendingAuth();
    await resume?.();
  };

  const onSubmit = async (data: McpServiceAuthFormData) => {
    try {
      const response = await connectTool({
        toolId: pendingAuth.mcpServiceId,
        payload: { username: data.username.trim(), password: data.password },
      });

      if (!response.connected) {
        throw new Error(response.error || t('INVALID_CREDENTIALS') || 'Invalid credentials');
      }

      await afterConnected();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('CONNECTION_FAILED') || 'Connection failed',
        description:
          error instanceof Error ? error.message : t('INVALID_CREDENTIALS') || 'Invalid credentials.',
      });
    }
  };

  // The event carries only authMethod; fetch the IdP URL from the start endpoint.
  const openOidcPopup = async () => {
    try {
      const { authorizationUrl } = await startToolOidc({
        toolId: pendingAuth.mcpServiceId,
        conversationId: pendingAuth.conversationId || activeConversationId || undefined,
      });
      if (!authorizationUrl) {
        throw new Error(t('SOMETHING_WENT_WRONG') || 'Something went wrong.');
      }
      const popup = window.open(authorizationUrl, 'mcp-oauth', 'width=520,height=680');
      if (!popup) {
        toast({
          variant: 'destructive',
          title: t('POPUP_BLOCKED') || 'Popup blocked',
          description: t('ALLOW_POPUPS_TO_CONNECT') || 'Please allow popups to connect.',
        });
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('CONNECTION_FAILED') || 'Connection failed',
        description:
          error instanceof Error ? error.message : t('SOMETHING_WENT_WRONG') || 'Something went wrong.',
      });
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && clearPendingAuth()}>
      <DialogContent className="max-w-[440px] rounded-[0.5rem] p-4">
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-lg font-medium leading-none">
            {t('CONNECT_TO_MCP_SERVICE', {
              name: pendingAuth.mcpServiceName,
              defaultValue: 'Connect to {{name}}',
            }) || 'Connect'}
          </DialogTitle>
          <DialogDescription className="text-[13px] leading-6">
            {isOidc
              ? t('MCP_SERVICE_OIDC_REQUIRED_DESCRIPTION') ||
                'This service uses single sign-on. Continue to your identity provider to authorize access.'
              : t('MCP_SERVICE_AUTH_REQUIRED_DESCRIPTION') ||
                'This service needs your credentials before the assistant can continue. They are sent securely and never shared with the model.'}
          </DialogDescription>
        </DialogHeader>

        {isOidc ? (
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => clearPendingAuth()}>
              {t('CANCEL') || 'Cancel'}
            </Button>
            <Button type="button" onClick={() => void openOidcPopup()} disabled={isStartingOidc}>
              {isStartingOidc ? t('CONNECTING') || 'Connecting...' : t('CONTINUE_TO_SIGN_IN') || 'Continue to sign in'}
            </Button>
          </DialogFooter>
        ) : (
          <Form {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 py-2">
              <FormField
                control={control}
                name="username"
                rules={{ required: t('USERNAME_REQUIRED') || 'Username is required' }}
                render={({ field }) => (
                  <FormItem>
                    <Label className="text-[13px] font-medium leading-6">
                      {t('USERNAME') || 'Username'}
                      <span className="text-destructive ml-0.5">*</span>
                    </Label>
                    <FormControl>
                      <Input
                        {...field}
                        autoFocus
                        className="h-10"
                        placeholder={t('ENTER_USERNAME') || 'Enter username'}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="password"
                rules={{ required: t('PASSWORD_REQUIRED') || 'Password is required' }}
                render={({ field }) => (
                  <FormItem>
                    <Label className="text-[13px] font-medium leading-6">
                      {t('PASSWORD') || 'Password'}
                      <span className="text-destructive ml-0.5">*</span>
                    </Label>
                    <FormControl>
                      <Input
                        {...field}
                        type="password"
                        className="h-10"
                        placeholder={t('ENTER_PASSWORD') || 'Enter password'}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter className="gap-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => clearPendingAuth()}
                  disabled={isPending}
                >
                  {t('CANCEL') || 'Cancel'}
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending ? t('CONNECTING') || 'Connecting...' : t('CONNECT') || 'Connect'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default McpServiceAuthModal;
